import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { orderConfigurationService, AvailableProducts, OrderConfiguration as IOrderConfiguration, TotalCalculation } from '../../services/order-configuration';
import { OrderConfiguration as OrderConfigData } from './OrderData';
import { calculateOrderTotal } from '../../utils/orderCalculation';
import { formatCalendarDate } from '../../utils/dateTime';

interface FoodParcel {
  id: number;
  name: string;
  price: number;
  /** S = small (2 bundle slots), M/L = medium/large (3 bundle slots) */
  size?: 'S' | 'M' | 'L';
}

interface OrderConfigurationProps {
  foodParcel: FoodParcel;
  assignedParcelDay: any;
  onNext: () => void;
  onBack: () => void;
  onConfiguration: (data: OrderConfigData) => Promise<void>;
  onCancel?: () => void;
  cancelling?: boolean;
}

const OrderConfiguration: React.FC<OrderConfigurationProps> = ({
  foodParcel,
  assignedParcelDay,
  onNext,
  onBack,
  onConfiguration,
  onCancel,
  cancelling,
}) => {
  const [availableProducts, setAvailableProducts] = useState<AvailableProducts | null>(null);
  const [configuration, setConfiguration] = useState<IOrderConfiguration>({
    food_parcel_id: foodParcel.id,
    bundle_selections: [null, null, null],
    bread_selection: null,
    grocery_selections: [null, null],
    parcel_item_selections: {},
    addon_product_selections: {},
  });
  const [totalCalculation, setTotalCalculation] = useState<TotalCalculation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const maxBundles = foodParcel.size === 'S' ? 2 : 3;

  const calculateTotal = (newConfiguration?: IOrderConfiguration) => {
    try {
      const configToUse = newConfiguration || configuration;
      const configToUseSliced = {
        ...configToUse,
        bundle_selections: (configToUse.bundle_selections ?? []).slice(0, maxBundles),
      };

      // Validate we have required data
      if (!availableProducts || !foodParcel) {
        return; // Can't calculate without data
      }

      // Calculate synchronously using frontend data
      const calculation = calculateOrderTotal(
        configToUseSliced,
        availableProducts,
        foodParcel.price,
        foodParcel.name
      );

      setTotalCalculation(calculation);
    } catch (err) {
      console.error('Error calculating total:', err);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await orderConfigurationService.getAvailableProducts(foodParcel.id);
        setAvailableProducts(products);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to load products. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [foodParcel.id]);

  // Default parcel item selections: Grocery Package and Fruit & Veg Box selected by default
  useEffect(() => {
    if (!availableProducts?.parcel_items?.length) return;
    setConfiguration(prev => {
      const hasAnySelection = Object.values(prev.parcel_item_selections || {}).some(Boolean);
      if (hasAnySelection) return prev;
      const defaultSelections = { ...(prev.parcel_item_selections || {}) };
      availableProducts.parcel_items.forEach((item: { id: number; type?: string }) => {
        if (item.type === 'grocery_package' || item.type === 'fruit_veg_box') {
          defaultSelections[item.id] = true;
        }
      });
      return { ...prev, parcel_item_selections: defaultSelections };
    });
  }, [availableProducts]);

  // Calculate total when products are loaded or configuration changes
  useEffect(() => {
    if (availableProducts && foodParcel) {
      calculateTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProducts, foodParcel, configuration]);

  const handleBundleSelection = (index: number, bundleId: number | null) => {
    const newBundleSelections = [...configuration.bundle_selections];
    newBundleSelections[index] = bundleId;
    
    const newConfiguration = {
      ...configuration,
      bundle_selections: newBundleSelections,
    };
    
    setConfiguration(newConfiguration);
    calculateTotal(newConfiguration);
  };

  const handleBreadSelection = (breadId: number | null) => {
    const newConfiguration = {
      ...configuration,
      bread_selection: breadId,
    };
    
    setConfiguration(newConfiguration);
    calculateTotal(newConfiguration);
  };

  const handleGrocerySelection = (index: number, productId: number | null) => {
    const newGrocerySelections = [...configuration.grocery_selections];
    newGrocerySelections[index] = productId;
    
    const newConfiguration = {
      ...configuration,
      grocery_selections: newGrocerySelections,
    };
    
    setConfiguration(newConfiguration);
    calculateTotal(newConfiguration);
  };

  const handleParcelItemSelection = (itemId: number, selected: boolean) => {
    const newConfiguration = {
      ...configuration,
      parcel_item_selections: {
        ...configuration.parcel_item_selections,
        [itemId]: selected,
      },
    };
    
    setConfiguration(newConfiguration);
    calculateTotal(newConfiguration);
  };

  const handleAddonQuantityChange = (productId: number, quantity: number) => {
    const qty = Math.max(0, Math.floor(quantity));
    const next = { ...(configuration.addon_product_selections || {}), [productId]: qty };
    if (qty === 0) delete next[productId];
    const newConfiguration = {
      ...configuration,
      addon_product_selections: next,
    };
    setConfiguration(newConfiguration);
    calculateTotal(newConfiguration);
  };

  const handleContinue = async () => {
    try {
      await onConfiguration({
        configuration: {
          ...configuration,
          bundle_selections: configuration.bundle_selections.slice(0, maxBundles),
        },
        totalCalculation,
      });
      onNext();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Failed to update configuration:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!availableProducts) {
    return <Alert severity="info">No products available for configuration.</Alert>;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {foodParcel.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Price: ${Number(foodParcel.price).toFixed(2)}
          </Typography>
          {assignedParcelDay && (
            <Typography variant="body2" color="text.secondary">
              Pickup on {formatCalendarDate(assignedParcelDay.date)}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Product Bundles
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Choose up to {maxBundles} bundle{maxBundles !== 1 ? 's' : ''} for your order
            </Typography>
            
            {/* AUTOCOMPLETE IMPLEMENTATION - Handles long text wrapping */}
            {Array.from({ length: maxBundles }, (_, i) => i).map((index) => {
              const sortedBundles = [...availableProducts.bundles].sort((a, b) => {
                const priceA = Number(a.bundle_price ?? 0);
                const priceB = Number(b.bundle_price ?? 0);
                if (priceA !== priceB) return priceA - priceB;
                return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
              });
              const bundleOptions = [
                { id: null, name: "No thanks. I don't need this", bundle_price: 0 },
                ...sortedBundles
              ];
              
              const selectedBundle = configuration.bundle_selections[index]
                ? availableProducts.bundles.find(b => b.id === configuration.bundle_selections[index])
                : null;
              
              return (
                <Autocomplete
                  key={index}
                  fullWidth
                  options={bundleOptions}
                  value={selectedBundle}
                  onChange={(event, newValue) => {
                    handleBundleSelection(index, newValue?.id || null);
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.trim();
                    
                    // If no input text, show all options
                    if (!inputValue) {
                      return options;
                    }
                    
                    // Check if inputValue matches any existing option name exactly
                    // (This means user clicked dropdown with existing selection, not typing)
                    const matchesExistingOption = options.some(
                      option => option.name.toLowerCase() === inputValue.toLowerCase()
                    );
                    
                    if (matchesExistingOption) {
                      return options; // Show all options when dropdown opened with selection
                    }
                    
                    // User is actively typing - filter options excluding "No thanks"
                    return options.filter(option => {
                      if (option.id === null) {
                        return false; // Don't show "No thanks" in search results
                      }
                      return option.name.toLowerCase().includes(inputValue.toLowerCase());
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Bundle ${index + 1}`}
                      placeholder="Select a bundle or type to search..."
                      multiline
                      minRows={1}
                      maxRows={4}
                      sx={{
                        '& .MuiInputBase-root': {
                          alignItems: 'flex-start',
                        },
                        '& .MuiAutocomplete-input': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || `none-${index}`}>
                      {option.id === null ? (
                        <em>{option.name}</em>
                      ) : (
                        <Box sx={{ width: '100%', py: 0.5 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              mb: 0.5
                            }}
                          >
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${option.bundle_price > 0 ? Number(option.bundle_price).toFixed(2) : '0.00'}
                          </Typography>
                        </Box>
                      )}
                    </li>
                  )}
                  ListboxProps={{
                    sx: {
                      maxHeight: 400,
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        alignItems: 'flex-start',
                      }
                    }
                  }}
                  sx={{ mb: 2 }}
                />
              );
            })}
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bread Selection
            </Typography>
            
            {/* AUTOCOMPLETE IMPLEMENTATION - Handles long text wrapping */}
            <Autocomplete
              fullWidth
              options={[
                { id: null, name: "No thanks. I don't need this", food_parcel_price: 0 },
                ...availableProducts.breads
              ]}
              value={
                configuration.bread_selection
                  ? availableProducts.breads.find(b => b.id === configuration.bread_selection) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleBreadSelection(newValue?.id || null);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              filterOptions={(options, state) => {
                const inputValue = state.inputValue.trim();
                
                // If no input text, show all options
                if (!inputValue) {
                  return options;
                }
                
                // Check if inputValue matches any existing option name exactly
                // (This means user clicked dropdown with existing selection, not typing)
                const matchesExistingOption = options.some(
                  option => option.name.toLowerCase() === inputValue.toLowerCase()
                );
                
                if (matchesExistingOption) {
                  return options; // Show all options when dropdown opened with selection
                }
                
                // User is actively typing - filter options excluding "No thanks"
                return options.filter(option => {
                  if (option.id === null) {
                    return false; // Don't show "No thanks" in search results
                  }
                  return option.name.toLowerCase().includes(inputValue.toLowerCase());
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bread"
                  placeholder="Select bread or type to search..."
                  multiline
                  minRows={1}
                  maxRows={4}
                  sx={{
                    '& .MuiInputBase-root': {
                      alignItems: 'flex-start',
                    },
                    '& .MuiAutocomplete-input': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id || 'none'}>
                  {option.id === null ? (
                    <em>{option.name}</em>
                  ) : (
                    <Box sx={{ width: '100%', py: 0.5 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          mb: 0.5
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ${Number(option.food_parcel_price).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </li>
              )}
              ListboxProps={{
                sx: {
                  maxHeight: 400,
                  '& .MuiAutocomplete-option': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    alignItems: 'flex-start',
                  }
                }
              }}
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Grocery Items
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Choose up to 2 grocery items
            </Typography>
            
            {/* AUTOCOMPLETE IMPLEMENTATION - Handles long text wrapping */}
            {[0, 1].map((index) => {
              const sortedGrocery = [...availableProducts.grocery_items].sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
              );
              const groceryOptions = [
                { id: null, name: "No thanks. I don't need this", food_parcel_price: 0 },
                ...sortedGrocery
              ];
              
              const selectedGrocery = configuration.grocery_selections[index]
                ? availableProducts.grocery_items.find(g => g.id === configuration.grocery_selections[index])
                : null;
              
              return (
                <Autocomplete
                  key={index}
                  fullWidth
                  options={groceryOptions}
                  value={selectedGrocery}
                  onChange={(event, newValue) => {
                    handleGrocerySelection(index, newValue?.id || null);
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.trim();
                    
                    // If no input text, show all options
                    if (!inputValue) {
                      return options;
                    }
                    
                    // Check if inputValue matches any existing option name exactly
                    // (This means user clicked dropdown with existing selection, not typing)
                    const matchesExistingOption = options.some(
                      option => option.name.toLowerCase() === inputValue.toLowerCase()
                    );
                    
                    if (matchesExistingOption) {
                      return options; // Show all options when dropdown opened with selection
                    }
                    
                    // User is actively typing - filter options excluding "No thanks"
                    return options.filter(option => {
                      if (option.id === null) {
                        return false; // Don't show "No thanks" in search results
                      }
                      return option.name.toLowerCase().includes(inputValue.toLowerCase());
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Grocery Item ${index + 1}`}
                      placeholder="Select grocery or type to search..."
                      multiline
                      minRows={1}
                      maxRows={4}
                      sx={{
                        '& .MuiInputBase-root': {
                          alignItems: 'flex-start',
                        },
                        '& .MuiAutocomplete-input': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || `none-${index}`}>
                      {option.id === null ? (
                        <em>{option.name}</em>
                      ) : (
                        <Box sx={{ width: '100%', py: 0.5 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              mb: 0.5
                            }}
                          >
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${Number(option.food_parcel_price).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </li>
                  )}
                  ListboxProps={{
                    sx: {
                      maxHeight: 400,
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        alignItems: 'flex-start',
                      }
                    }
                  }}
                  sx={{ mb: 2 }}
                />
              );
            })}
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Optional Items
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Add these optional items to your order (e.g. Grocery Package, Fruit & Veg Box).
            </Typography>
            {availableProducts.parcel_items.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={configuration.parcel_item_selections[item.id] || false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleParcelItemSelection(item.id, e.target.checked)}
                  />
                }
                label={`${item.name} - $${Number(item.price).toFixed(2)}`}
              />
            ))}
          </Paper>

          {availableProducts.addon_products && availableProducts.addon_products.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Add-ons
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Products from our list you can add to your order. Grouped by category.
              </Typography>
              {(() => {
                const categoryMap = new Map<number, { name: string; products: typeof availableProducts.addon_products }>();
                availableProducts.addon_products!.forEach((p) => {
                  const ids = p.category_ids || [];
                  const names = p.category_names || [];
                  if (ids.length === 0) {
                    const uncat = 0;
                    if (!categoryMap.has(uncat)) categoryMap.set(uncat, { name: 'Other', products: [] });
                    categoryMap.get(uncat)!.products!.push(p);
                  } else {
                    ids.forEach((catId, i) => {
                      const name = names[i] || `Category ${catId}`;
                      if (!categoryMap.has(catId)) categoryMap.set(catId, { name, products: [] });
                      categoryMap.get(catId)!.products!.push(p);
                    });
                  }
                });
                const categories = Array.from(categoryMap.entries());
                return categories.map(([catId, { name, products }]) => {
                  const sortedProducts = [...(products || [])].sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
                  );
                  return (
                  <Accordion key={catId} defaultExpanded={categories.length <= 3}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">{name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {sortedProducts.map((p) => {
                        const qty = (configuration.addon_product_selections || {})[p.id] ?? 0;
                        const maxQty = Math.max(0, p.stock_quantity ?? 0);
                        return (
                          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {p.name} — ${Number(p.add_on_price).toFixed(2)} each
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={qty}
                              onChange={(e) => handleAddonQuantityChange(p.id, parseInt(e.target.value, 10) || 0)}
                              inputProps={{ min: 0, max: maxQty, step: 1 }}
                              sx={{ width: 80 }}
                              InputProps={{
                                inputProps: { min: 0, max: maxQty },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                  );
                });
              })()}
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: '20px' }}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <Divider sx={{ my: 2 }} />
            {totalCalculation ? (
              <Box>
                <Typography variant="h5" color="primary" gutterBottom>
                  Total: ${Number(totalCalculation.total).toFixed(2)}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" gutterBottom>
                  {totalCalculation.breakdown.food_parcel.name}: ${Number(totalCalculation.breakdown.food_parcel.price).toFixed(2)}
                </Typography>
                {totalCalculation.breakdown.bundles && (
                  <Typography variant="body2" gutterBottom>
                    Bundles ({totalCalculation.breakdown.bundles.count}): ${Number(totalCalculation.breakdown.bundles.total).toFixed(2)}
                  </Typography>
                )}
                {totalCalculation.breakdown.bread && (
                  <Typography variant="body2" gutterBottom>
                    {totalCalculation.breakdown.bread.name}: ${Number(totalCalculation.breakdown.bread.price).toFixed(2)}
                  </Typography>
                )}
                {totalCalculation.breakdown.grocery && (
                  <Box>
                    {totalCalculation.breakdown.grocery.items.map((item, index) => (
                      <Typography key={index} variant="body2" gutterBottom>
                        {item.name}: ${Number(item.price).toFixed(2)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {totalCalculation.breakdown.parcel_items && totalCalculation.breakdown.parcel_items.items.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Optional Items</Typography>
                    {totalCalculation.breakdown.parcel_items.items.map((item, index) => (
                      <Typography key={index} variant="body2" gutterBottom>
                        {item.name}: ${Number(item.price).toFixed(2)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {totalCalculation.breakdown.addon_products && totalCalculation.breakdown.addon_products.items.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Add-ons</Typography>
                    {totalCalculation.breakdown.addon_products.items.map((item, index) => (
                      <Typography key={index} variant="body2" gutterBottom>
                        {item.name} × {item.quantity}: ${Number(item.line_total).toFixed(2)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography>
                Total: ${Number(foodParcel.price).toFixed(2)}
              </Typography>
            )}
            <Box mt={3} display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Button onClick={onBack}>Back</Button>
              <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 160 }}>
                <Button onClick={handleContinue} variant="contained" color="primary" fullWidth>
                  Continue
                </Button>
                {onCancel && (
                  <Button variant="outlined" color="error" onClick={onCancel} disabled={cancelling} fullWidth>
                    {cancelling ? 'Cancelling...' : 'Cancel Order'}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderConfiguration; 